import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { useToast } from "../components/ui/Toast";
import { Lightbulb, Users, Workflow, Eye, Trash2, Loader2, ChevronDown, ChevronUp, MessageSquare, Plus, Search } from 'lucide-react';
import { useAdminApi } from '../apis/admin-apis';
import { useToolsApi } from '../apis/tools-apis';
import { useAgentsApi } from '../apis/agents-apis';
import { usePlaybooksApi } from '../apis/playbooks-apis';
import { usePromptsApi } from '../apis/prompts-apis';
import { AdminData } from '../types';
import { ConfirmationDialog } from '../components/ConfirmationDialog';

type ItemType = 'tool' | 'agent' | 'playbook' | 'prompt';

const formatItemType = (type: ItemType | undefined): string => {
  if (!type) return 'Item';
  return type.charAt(0).toUpperCase() + type.slice(1);
};

const CoreItemsPage: React.FC = () => {
  const adminApi = useAdminApi();
  const toolsApi = useToolsApi();
  const agentsApi = useAgentsApi();
  const playbooksApi = usePlaybooksApi();
  const promptsApi = usePromptsApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const { addToast } = useToast();
  const [expandedSection, setExpandedSection] = useState<ItemType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{id: string; type: ItemType} | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const response = await adminApi.getAdminData();
        setAdminData(response);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        addToast({
          title: "Error",
          description: "Failed to load administrative data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [addToast]);

  const handleView = (type: ItemType, id: string) => {
    navigate(`/studio/${type}s/edit/${id}`);
  };

  const confirmDelete = (type: ItemType, id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setItemToDelete({ type, id });
    setDeleteDialogOpen(true);
  };

  const getDeleteFunction = (type: ItemType) => {
    switch (type) {
      case 'tool':
        return toolsApi.deleteTool;
      case 'agent':
        return agentsApi.deleteAgent;
      case 'playbook':
        return playbooksApi.deletePlaybook;
      case 'prompt':
        return promptsApi.deletePrompt;
      default:
        throw new Error(`Unknown type: ${type}`);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!itemToDelete) return;

    setIsDeletingId(itemToDelete.id);
    try {
      const deleteFunction = getDeleteFunction(itemToDelete.type);
      await deleteFunction(itemToDelete.id);
      
      addToast({
        title: "Success",
        description: `${formatItemType(itemToDelete.type)} successfully deleted`,
        variant: "default",
      });

      // Refresh admin data
      const response = await adminApi.getAdminData();
      setAdminData(response);
    } catch (error) {
      console.error(`Error deleting ${itemToDelete.type}:`, error);
      addToast({
        title: "Error",
        description: `Failed to delete ${itemToDelete.type}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const ItemCard: React.FC<{
    title: string;
    description: string;
    id: string;
    type: ItemType;
    tags?: string[];
    createdAt?: string;
  }> = ({ title, description, id, type, tags, createdAt }) => (
    <div 
      className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-150 h-full flex flex-col"
      onClick={() => handleView(type, id)}
    >
      <div className="p-5 flex-grow">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-1 line-clamp-2">
              {title}
            </h3>
            {createdAt && (
              <p className="text-xs text-gray-500 mb-2">
                Created {new Date(createdAt).toLocaleDateString()}
              </p>
            )}
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {description || 'No description provided'}
            </p>
          </div>
        </div>

        {tags && tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                onClick={(e) => e.stopPropagation()}
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span 
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                onClick={(e) => e.stopPropagation()}
              >
                +{tags.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
      <div 
        className="bg-gray-50 px-5 py-3 flex justify-end space-x-2 border-t border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => handleView(type, id)}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Eye className="-ml-0.5 mr-2 h-4 w-4" />
          View
        </button>
        <button
          onClick={(e) => confirmDelete(type, id, e)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          disabled={isDeletingId === id}
        >
          {isDeletingId === id ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="-ml-0.5 mr-2 h-4 w-4" />
          )}
          {isDeletingId === id ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  );

  const Section: React.FC<{
    title: string;
    icon: React.ReactNode;
    items: any[];
    type: ItemType;
    color?: string;
  }> = ({ title, icon, items, type, color = 'gray' }) => {
    const isExpanded = expandedSection === type;
    const filteredItems = items
      .filter(item => 
        searchQuery === '' || 
        item[`${type}Name`]?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item[`${type}Description`]?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item[`${type}Tags`]?.some((tag: string) => 
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    const recentItems = [...filteredItems]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    return (
      <div 
        className={`bg-white overflow-hidden shadow rounded-lg border border-${color}-100 transition-all duration-200 ${
          isExpanded ? 'ring-2 ring-indigo-500' : 'hover:shadow-md'
        }`}
      >
        <div 
          className="px-6 py-4 cursor-pointer"
          onClick={() => setExpandedSection(isExpanded ? null : type)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg bg-${color}-50`}>
                {icon}
              </div>
              <h2 className="text-lg font-medium text-gray-900">{title}</h2>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {filteredItems.length} items
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/studio/${type}s/add`);
                }}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="-ml-1 mr-1 h-4 w-4" />
                New
              </button>
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            {recentItems.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {recentItems.map((item) => (
                  <ItemCard
                    key={item[`${type}Id`]}
                    id={item[`${type}Id`]}
                    type={type}
                    title={item[`${type}Name`]}
                    description={item[`${type}Description`]}
                    tags={item[`${type}Tags`]}
                    createdAt={item.createdAt}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No {title.toLowerCase()} found{searchQuery ? ' matching your search' : ''}</p>
                <button
                  onClick={() => navigate(`/studio/${type}s/add`)}
                  className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Plus className="-ml-1 mr-2 h-4 w-4" />
                  Create your first {title.toLowerCase()}
                </button>
              </div>
            )}
            
            {filteredItems.length > 5 && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate(`/studio/${type}s`)}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                  View all {filteredItems.length} {title.toLowerCase()}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="w-8 h-8" />
          <p className="text-gray-600">Loading administrative data...</p>
        </div>
      </div>
    );
  }

  if (!adminData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-xl text-gray-600">No administrative data available</p>
      </div>
    );
  }

  const formattedItemType = formatItemType(itemToDelete?.type);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="pb-5 border-b border-gray-200 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Core Items</h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage your tools, agents, playbooks, and prompts in one place
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md h-10"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                  <Lightbulb className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Tools</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {adminData?.tools.length || 0}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Agents</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {adminData?.agents.length || 0}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                  <Workflow className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Playbooks</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {adminData?.playbooks.length || 0}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Prompts</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {adminData?.prompts.length || 0}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          <Section
            title="Tools"
            icon={<Lightbulb className="w-5 h-5 text-yellow-500" />}
            items={adminData.tools || []}
            type="tool"
            color="yellow"
          />
          <Section
            title="Agents"
            icon={<Users className="w-5 h-5 text-blue-500" />}
            items={adminData.agents || []}
            type="agent"
            color="blue"
          />
          <Section
            title="Playbooks"
            icon={<Workflow className="w-5 h-5 text-purple-500" />}
            items={adminData.playbooks || []}
            type="playbook"
            color="purple"
          />
          <Section
            title="Prompts"
            icon={<MessageSquare className="w-5 h-5 text-green-500" />}
            items={adminData.prompts || []}
            type="prompt"
            color="green"
          />
        </div>
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title={`Delete ${formattedItemType}`}
        description={`This will permanently delete the ${itemToDelete?.type || 'item'} from the system. This action cannot be undone.`}
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />
    </div>
  );
};

export default CoreItemsPage;